import type { MaybeComputedElementRef } from '@vueuse/core';
import type { MaybeRefOrGetter } from 'vue';
import type { CalendarRange } from './build-calendar-range-filter';
import type { CalendarEventSpan } from './calendar-events';
import { unrefElement } from '@vueuse/core';
import { Temporal } from 'temporal-polyfill';
import { computed, toValue, watch } from 'vue';
import { CALENDAR_TIME_GRID_HEIGHT } from '#layers/listing/constants/calendar';
import { isDateInRange } from './build-calendar-range-filter';
import { isSpanning } from './calendar-events';

const DEFAULT_START_HOUR = 8;

// schedule-x draws a new view a frame or two after it mounts, so the scroll waits up to this many frames.
const MAX_DRAW_WAIT_FRAMES = 30;

interface UseTimeGridScrollOptions {
	root: MaybeComputedElementRef;
	isActive: MaybeRefOrGetter<boolean>;
	isSettled: MaybeRefOrGetter<boolean>;
	events: MaybeRefOrGetter<readonly CalendarEventSpan[]>;
	range: MaybeRefOrGetter<CalendarRange>;
}

/** Scrolls Week and Day to their first timed item once per range, so the user's own scroll stays. */
export function useTimeGridScroll({ root, isActive, isSettled, events, range }: UseTimeGridScrollOptions) {
	const startHour = computed(() => {
		const visibleRange = toValue(range);
		let earliest: number | undefined;

		for (const event of toValue(events)) {
			if (!(event.start instanceof Temporal.ZonedDateTime) || isSpanning(event) || !isDateInRange(event.start.toPlainDate(), visibleRange)) continue;
			earliest = Math.min(earliest ?? event.start.hour, event.start.hour);
		}

		return earliest === undefined ? DEFAULT_START_HOUR : Math.max(0, earliest - 1);
	});

	let scrolledRangeKey: string | undefined;

	// Queries schedule-x's scroller directly, because its scroll controller is a separate package.
	function scrollWhenDrawn(framesLeft: number) {
		const container = unrefElement(root)?.querySelector('.sx__view-container');
		if (!toValue(isActive) || !(container instanceof HTMLElement)) return;

		// Leaves the range to the next settle when a load started after this scroll was planned, since its items are not in yet.
		if (!toValue(isSettled)) {
			scrolledRangeKey = undefined;
			return;
		}

		if (!container.querySelector('.sx__week-grid')) {
			if (framesLeft > 0) requestAnimationFrame(() => scrollWhenDrawn(framesLeft - 1));
			return;
		}

		container.scrollTo({ top: startHour.value * CALENDAR_TIME_GRID_HEIGHT / 24, behavior: 'instant' });
	}

	watch(
		() => {
			const { start, end } = toValue(range);
			return { element: unrefElement(root), rangeKey: `${start}:${end}`, isReady: toValue(isActive) && toValue(isSettled) };
		},
		({ element, rangeKey, isReady }, previous) => {
			// Starts over after leaving Week and Day or a remount, so the next time grid opens at its items again.
			if (!toValue(isActive) || element !== previous?.element) scrolledRangeKey = undefined;
			if (!element || !isReady || rangeKey === scrolledRangeKey) return;

			scrolledRangeKey = rangeKey;
			requestAnimationFrame(() => scrollWhenDrawn(MAX_DRAW_WAIT_FRAMES));
		},
		// Runs after rstore's pre-flush query watcher, so a new range reads the load it starts instead of the last settle.
		{ immediate: true, flush: 'post' },
	);
}
